/**
 * Add files to a deal that already exists (DOP-608).
 *
 * Every way in — the sidebar's + button and menu, a drop onto a deal, and the
 * Data Sources chooser — opens the same review dialog through this context, so
 * the rules for what gets sent live in one place. The upload itself goes to the
 * app-wide upload manager against the deal's own project and folder, exactly
 * like a first upload, so the tray, the spinner and the backend re-ingest
 * behave the same.
 */

import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

import AddFilesDialog from "../components/AddFiles/AddFilesDialog";
import type { FolderTreeNode } from "../services/fileApi";
import { useToast } from "./ToastContext";
import { useUploadManager } from "./UploadManager";
import { canWriteFolder } from "../utils/folderSharing";

export interface OpenAddFilesInput {
	deal: FolderTreeNode;
	/** Files already picked (a drop, or the Data Sources picker). */
	files?: File[];
	/**
	 * After the files were handed to the upload manager, with exactly the
	 * files sent: a copy kept beside a same-named file arrives renamed, and a
	 * caller watching for its file must look for the name that was sent.
	 */
	onAdded?: (sent: File[]) => void;
	/** Offers "Pick a different deal", handing back the files under review. */
	onChangeDeal?: (files: File[]) => void;
}

interface AddFilesContextValue {
	openAddFiles: (input: OpenAddFilesInput) => void;
}

const AddFilesContext = createContext<AddFilesContextValue | undefined>(
	undefined
);

/**
 * `null` outside a provider (a component rendered alone in a unit test): the
 * add-files affordances are then simply not offered, rather than offered and
 * broken.
 */
export const useAddFiles = (): AddFilesContextValue | null =>
	useContext(AddFilesContext) ?? null;

/** A deal can take files only when it belongs to a project. */
export const canAddFilesTo = (deal: FolderTreeNode): boolean =>
	Boolean(deal.folder.is_root && deal.folder.project_id) &&
	// A deal shared with the caller view-only takes no uploads.
	canWriteFolder(deal.folder);

export const AddFilesProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { enqueueUpload } = useUploadManager();
	const { showToast } = useToast();
	const [request, setRequest] = useState<(OpenAddFilesInput & { key: number }) | null>(null);

	const openAddFiles = useCallback(
		(input: OpenAddFilesInput) => {
			// Every way in already offers only deals that take files; this is the
			// last word, so a view-only shared deal can never get an upload.
			if (!canAddFilesTo(input.deal)) {
				showToast("This deal is shared with you view-only.", "error");
				return;
			}
			// A fresh key per open: a second open never inherits the last review.
			setRequest({ ...input, key: Date.now() });
		},
		[showToast],
	);

	const close = useCallback(() => setRequest(null), []);

	const handleConfirm = useCallback(
		(files: File[]) => {
			if (!request) return;
			const { deal } = request;
			const projectId = deal.folder.project_id;
			if (!projectId) {
				showToast(`Files can't be added to "${deal.folder.name}".`);
				return;
			}
			enqueueUpload({
				projectId,
				projectName: deal.folder.name,
				folderId: deal.folder.id,
				reportingPeriod: "",
				files,
			});
			showToast(
				`Adding ${files.length} file${files.length === 1 ? "" : "s"} to ${deal.folder.name}. You can keep working while they upload.`,
				"success"
			);
			setRequest(null);
			request.onAdded?.(files);
		},
		[enqueueUpload, request, showToast]
	);

	const handleChangeDeal = useCallback(
		(files: File[]) => {
			const onChangeDeal = request?.onChangeDeal;
			setRequest(null);
			onChangeDeal?.(files);
		},
		[request]
	);

	const value = useMemo(() => ({ openAddFiles }), [openAddFiles]);

	return (
		<AddFilesContext.Provider value={value}>
			{children}
			{request && (
				<AddFilesDialog
					key={request.key}
					deal={request.deal}
					initialFiles={request.files ?? []}
					onClose={close}
					onConfirm={handleConfirm}
					onChangeDeal={request.onChangeDeal ? handleChangeDeal : undefined}
				/>
			)}
		</AddFilesContext.Provider>
	);
};
